<script setup>
	import { ref, computed, onMounted, onUnmounted } from 'vue';
	import { useRouter } from 'vue-router';
	import { useI18n } from 'vue-i18n';
	import { useMapStoreData, useFavoritesStore } from '@/stores/index';
	import Header from '@/components/Header.vue';
	import { trackMapModuleFromHome } from '@/utils/trackModuleAnalytics';
	const { t } = useI18n();
	const mapStore = useMapStoreData();
	const favoritesStore = useFavoritesStore();
	const router = useRouter();
	const preventClick = ref(true);
	const homeContainer = ref(null);
	const setupDataHeader = {
		centerIcon: false,
		orderFilter: false,
		search: false,
		backArrow: false,
		backArrowMap: false,
		shortHeader: false,
		hideLanguage: false,
	};
	const backgroundImageUrl = (item) => {
		return `url(${mapStore.publicUrl}repo/${mapStore.destinationId}/${item.thumb.hash}.${item.thumb.ext})`;
	};

	const hideHome = () => {
		mapStore.hideMap = false;
		mapStore.showMap = true;
	};

	/** Open map from the main "map" tile – restores full POI list (clears mapCopy filter). */
	const openMapFromMainMap = () => {
		if (preventClick.value) return;
		mapStore.openMapDefault();
		favoritesStore.switchFavoritesModule(null);
		trackMapModuleFromHome('map');
	};

	const navigateTo = (path, index) => {
		if (preventClick.value) return;
		if (path == null || path === '') return;
		router.push(`${path}/${index ?? ''}`);
	};

	const onModuleClick = (item, index) => {
		if (preventClick.value) return;
		// Map tile: type "map"/"mapCopy" (case-insensitive) OR module has "pois" or "tag"/"tags"
		const typeLower = typeof item.type === 'string' ? item.type.toLowerCase() : '';
		const hasPois = Array.isArray(item.pois) && item.pois.length > 0;
		const hasTagFilter = item.tag != null || (Array.isArray(item.tags) && item.tags.length > 0);
		const isMapCopy = typeLower === 'map' || typeLower === 'mapcopy' || hasPois || hasTagFilter;
		if (isMapCopy) {
			const useFavorites = item.use_favorites === true;
			const moduleKey = item.id ?? `${item.title ?? 'module'}-${index}`;
			if (hasPois) {
				mapStore.openMapWithPoiFilter(item.pois, item.active_poi, item.active_path, item.title, moduleKey, useFavorites);
			} else if (hasTagFilter) {
				const tagOrTags = Array.isArray(item.tags) ? item.tags : item.tag;
				const poiIds = mapStore.getPoiIdsByTag(tagOrTags);
				mapStore.openMapWithPoiFilter(poiIds, item.active_poi, item.active_path, item.title, moduleKey, useFavorites);
			} else {
				mapStore.openMapDefault();
				favoritesStore.switchFavoritesModule(null);
				trackMapModuleFromHome('map');
				return;
			}
			const favKey = mapStore.mapCopyUseFavorites ? mapStore.mapCopyModuleKey : null;
			favoritesStore.switchFavoritesModule(favKey);
			trackMapModuleFromHome('mapCopy', item, index);
		} else if (item.type != null && item.type !== '') {
			navigateTo(item.type, index);
		}
	};

	const shouldShowItem = computed(() => {
		return (item) => item.type !== 'iframe' || useMapStoreData().isOnline;
	});

	const homeTiles = computed(() => {
		const tiles = [];
		const modules = mapStore.modules ? Array.from(mapStore.modules.entries()) : [];

		// Build tile order from configured module order (includes map/scenarios).
		if (modules.length > 0) {
			let routeIndex = 0;
			modules.forEach(([key, value], moduleIndex) => {
				const base = {
					key: value?.id ?? `tile-${key}-${moduleIndex}`,
					order: typeof value?.order === 'number' ? value.order : moduleIndex,
				};

				if (key === 'map') {
					tiles.push({ ...base, kind: 'map', item: value });
					return;
				}
				if (key === 'scenarios') {
					tiles.push({ ...base, kind: 'scenarios', item: value });
					return;
				}

				tiles.push({
					...base,
					kind: 'route',
					item: value,
					index: routeIndex,
				});
				routeIndex++;
			});
			return tiles.sort((a, b) => a.order - b.order);
		}

		// Fallback when module config is missing.
		tiles.push({ kind: 'map', key: 'tile-map', order: -20 });
		tiles.push({ kind: 'scenarios', key: 'tile-scenarios', order: -10 });
		mapStore.routes.forEach((item, index) => {
			tiles.push({
				kind: 'route',
				key: item.id ?? `tile-route-${index}`,
				order: typeof item.order === 'number' ? item.order : index,
				item,
				index,
			});
		});
		return tiles.sort((a, b) => a.order - b.order);
	});

	const visibleHomeTiles = computed(() => {
		return homeTiles.value.filter((tile) => {
			if (tile.kind === 'map') return useMapStoreData().hideMapFromHome;
			if (tile.kind === 'scenarios') return true;
			return shouldShowItem.value(tile.item);
		});
	});

	const tileStyle = (tile, visibleIndex) => {
		const isLastVisible = visibleIndex === visibleHomeTiles.value.length - 1;
		if (tile.kind === 'map') {
			return {
				backgroundImage: `url(${mapStore.mapImage})`,
				marginLeft: '-2.34375vw',
			};
		}
		if (tile.kind === 'scenarios') {
			return {
				backgroundImage: `url(${mapStore.scenariosImage})`,
				marginLeft: '-2.34375vw',
				borderRadius: isLastVisible ? '0' : '',
			};
		}
		return {
			backgroundImage: backgroundImageUrl(tile.item),
			borderRadius: isLastVisible ? '0' : '',
		};
	};

	const onTileClick = (tile) => {
		if (tile.kind === 'map') {
			openMapFromMainMap();
			return;
		}
		if (tile.kind === 'scenarios') {
			navigateTo('/scenarios');
			return;
		}
		onModuleClick(tile.item, tile.index);
	};

	onMounted(() => {
		window.addEventListener('resize', setAppHeight);
		window.addEventListener('orientationchange', setAppHeight);
		setAppHeight();

		setTimeout(() => {
			preventClick.value = false;
		}, 300);
	});

	const setAppHeight = () => {
		document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
		if (homeContainer.value) {
			homeContainer.value.style.height = `${window.innerHeight}px`;
		}
	};

	onUnmounted(() => {
		window.removeEventListener('resize', setAppHeight);
		window.removeEventListener('orientationchange', setAppHeight);
	});
</script>

<template>
	<Transition>
		<div class="container" v-show="!mapStore.showMap">
			<Header :setupHeader="setupDataHeader" />
			<div class="home-container" ref="homeContainer">
				<div
					v-for="(tile, visibleIndex) in visibleHomeTiles"
					:key="tile.key"
					:class="[
						'menu-item',
						tile.kind === 'map' ? 'map' : tile.kind === 'scenarios' ? 'poi' : 'custom-items',
					]"
					@click="onTileClick(tile)"
					:style="tileStyle(tile, visibleIndex)"
				>
					<div class="holder">
						<div class="text">
							<div class="align">
								{{
									t(
										tile.item?.title ??
											(tile.kind === 'map'
												? 'landing_page.map.title'
												: tile.kind === 'scenarios'
													? 'landing_page.scenarios.title'
													: '')
									)
								}}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Transition>
</template>

<style scoped lang="scss">
	@import '../assets/style/mixins.scss';
	@import '../assets/style/_HomeView.scss';

	.main-header {
		position: absolute;
		top: 0;
		left: 0;
		width: get-vw(670px);
		height: get-vw(120px);
		z-index: 98;
		border-radius: 0 100px 100px 0;

		:deep(.container) {
			.menu {
				.language-container {
					right: 10px;
					top: get-vw(30px);
				}
				.home-menu-container {
					display: none;
				}
			}
		}
	}

	.loading {
		.progress-circle {
			max-width: 100px;
			margin: 20px;
		}
		svg {
			width: 100%;
			height: auto;
			transform: rotate(-90deg);
		}
		.circle-bg {
			fill: none;
			stroke: #eee;
			stroke-width: 3.8;
		}
		.circle {
			fill: none;
			stroke-width: 3.8;
			stroke-linecap: round;
			stroke: #f44336;
			transition: stroke-dasharray 0.35s ease;
		}
		.percentage {
			fill: #f44336;
			font-size: 0.5em;
			text-anchor: middle;
			dominant-baseline: middle;
		}
	}

	@media only screen and (min-width: 1080px) and (max-width: 1080px) and (min-height: 1920px) and (max-height: 1920px) {
		.destination-17 {
			.main-header {
				top: unset;
				bottom: 0;
				width: get-vw(670px);
				height: get-vw(120px);

				:deep(.container) {
					.menu {
						.language-container {
							top: get-vw(15px);
							right: get-vw(10px);
						}
					}
				}
			}
		}
	}
</style>
